import {
  Briefcase,
  Building2,
  Calendar as CalendarIcon,
  ChevronRight,
  ClipboardList,
  Code2,
  Home as HomeIcon,
  Inbox,
  Plus,
  RotateCw,
  Search,
  Settings,
  Sparkles,
  Users,
  User as UserIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useRefresh } from "ra-core";

// Sprint 7 T7.3 + Sprint 8 — TopBar harmonisée conforme à design/atomic-crm/
// contacts-app.jsx + dev/ent/cal-shared.jsx (Topbar). Crumbs hiérarchique
// 2-niveaux (Catégorie / Page), kbd ⌘K dans la search input, refresh isolé,
// create optionnel.

type RouteMeta = {
  /** Catégorie parent (Sprint 8). Si présente, affichée comme premier crumb. */
  category?: string;
  /** Crumb label affiché à droite de l'icône. */
  label: string;
  /** Icône lucide pour le crumb (15px). */
  icon: React.ReactNode;
  /** Si présent, ajoute un bouton create primary à droite. */
  createTo?: string;
  createLabel?: string;
};

const ROUTE_META: Record<string, RouteMeta> = {
  "/": { category: "Accueil", label: "Aujourd'hui", icon: <HomeIcon /> },
  "/my-day": { category: "Accueil", label: "Ma journée", icon: <HomeIcon /> },
  "/contacts": {
    category: "Commerce",
    label: "Contacts",
    icon: <Users />,
    // Sprint 7 T7.1.d — create retiré ici (ContactListActions in-page
    // l'expose en pill primary + kbd N).
  },
  "/companies": {
    category: "Commerce",
    label: "Entreprises",
    icon: <Building2 />,
    // Sprint 8.1 — create button retiré ici (CompanyPageHead in-page
    // l'expose dans la title row à droite, cf pattern Sprint 7 T7.1.d.2 fait
    // pour /contacts).
  },
  "/deals": {
    category: "Commerce",
    label: "Affaires",
    icon: <Briefcase />,
    createTo: "/deals/create",
    createLabel: "Nouvelle affaire",
  },
  "/appointments": {
    category: "Communication",
    label: "Calendrier",
    icon: <CalendarIcon />,
    createTo: "/appointments/create",
    createLabel: "Nouveau RDV",
  },
  "/dev_tasks": {
    category: "Produit",
    label: "Dev",
    icon: <Code2 />,
    createTo: "/dev_tasks/create",
    createLabel: "Nouveau ticket",
  },
  "/changelog": {
    category: "Produit",
    label: "Changelog",
    icon: <Sparkles />,
  },
  "/agent": { category: "Outils", label: "Agent", icon: <Sparkles /> },
  "/settings/email-accounts": {
    category: "Communication",
    label: "Boîte de réception",
    icon: <Inbox />,
  },
  "/settings": { category: "Admin", label: "Paramètres", icon: <Settings /> },
  "/tasks": {
    category: "Accueil",
    label: "Mes tâches",
    icon: <ClipboardList />,
  },
  "/profile": { category: "Admin", label: "Profil", icon: <UserIcon /> },
  "/sales": {
    category: "Admin",
    label: "Équipe",
    icon: <Users />,
    createTo: "/sales/create",
    createLabel: "Nouveau membre",
  },
};

const FALLBACK: RouteMeta = { label: "Atomic CRM", icon: <HomeIcon /> };

export function FabrikTopBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const meta =
    ROUTE_META[pathname] ??
    ROUTE_META["/" + pathname.split("/")[1]] ??
    FALLBACK;

  // Sprint 7 T7.3 — Raccourci ⌘K / Ctrl+K focus search input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    const filter = encodeURIComponent(JSON.stringify({ q }));
    navigate(`/contacts?filter=${filter}`);
  };

  return (
    <header className="h-14 shrink-0 border-b border-border bg-background/80 backdrop-blur-sm flex items-center gap-3 px-4 md:px-6 sticky top-0 z-10">
      {/* Crumbs hiérarchique : icone + Catégorie / Page (Sprint 8) */}
      <div className="flex items-center gap-2 min-w-0 shrink-0">
        <span className="text-muted-foreground [&>svg]:size-[15px]">
          {meta.icon}
        </span>
        {meta.category && (
          <>
            <span className="hidden sm:inline text-[12.5px] text-muted-foreground truncate">
              {meta.category}
            </span>
            <ChevronRight
              className="hidden sm:inline size-3 text-muted-foreground/50 shrink-0"
              aria-hidden
            />
          </>
        )}
        <span className="text-[13px] font-medium truncate">{meta.label}</span>
      </div>

      {/* Search avec kbd ⌘K */}
      <form
        onSubmit={handleSearch}
        className="hidden md:flex items-center gap-2 h-9 px-2.5 ml-auto md:ml-6 w-[300px] rounded-lg bg-muted/60 border border-border focus-within:border-[var(--accent-solid)]/40"
      >
        <Search className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher partout…"
          className="flex-1 bg-transparent outline-none text-[12.5px] placeholder:text-muted-foreground"
          aria-label="Rechercher"
        />
        {query ? (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="text-[10.5px] text-muted-foreground hover:text-foreground px-1"
            aria-label="Effacer"
          >
            ✕
          </button>
        ) : (
          <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-background border border-border text-muted-foreground font-mono">
            ⌘K
          </kbd>
        )}
      </form>

      <div className="md:hidden ml-auto" />

      {/* Refresh icon-btn */}
      <button
        type="button"
        onClick={() => refresh()}
        className="hidden sm:inline-flex size-9 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60"
        aria-label="Rafraîchir"
      >
        <RotateCw className="size-4" />
      </button>

      {/* Optional create button */}
      {meta.createTo && (
        <button
          type="button"
          onClick={() => navigate(meta.createTo!)}
          className="h-9 px-3 rounded-lg text-[12.5px] font-medium inline-flex items-center gap-1.5 text-white transition-opacity hover:opacity-90"
          style={{ background: "var(--accent-solid)" }}
        >
          <Plus className="size-4" /> {meta.createLabel ?? "Nouveau"}
        </button>
      )}
    </header>
  );
}
